/**
 * セッション状態管理モジュール
 *
 * 地図操作のたびにセッション状態をsession_stateテーブルに保存（UPSERT）。
 * shorturlテーブルへのINSERTを廃止し、DB肥大化を防ぐ。
 *
 * - saveSessionState: 現在の地図状態をサーバーに保存 + URLバー ?ss=1 に更新
 * - loadSessionState: uid+deviceでsession_stateから復元
 * - scheduleSessionSave: デバウンス付きで保存をスケジュール
 */
import axios from 'axios'
import { VPS_API_BASE } from '@/js/phpApi'

type VM = unknown

const SESSION_SAVE_DELAY = 2000 // 2秒デバウンス
let sessionSaveTimerId: number | null = null
let lastSavedParam: string | null = null

/**
 * デバイスIDを取得（localStorageに永続化）
 * ユーザー×デバイスでsession_stateの1行を特定するために使用
 */
export function getDeviceId(): string {
  let id = localStorage.getItem('oh3_device_id')
  if (!id) {
    id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).substring(2) + Date.now().toString(36)
    localStorage.setItem('oh3_device_id', id)
  }
  return id
}

/**
 * セッション状態をサーバーに保存
 * FormData方式でPOST（CORS対策）
 * 保存成功後、URLバーを ?ss=1 に更新
 */
export function saveSessionState(vm: VM): void {
  const uid = localStorage.getItem('lastUserId')
  if (!uid) return // 未ログインは保存しない

  // @ts-ignore
  const currentParam = vm.param
  if (!currentParam || currentParam === lastSavedParam) return // 変更なしならスキップ

  const formData = new FormData()
  formData.append('uid', uid)
  formData.append('device', getDeviceId())
  formData.append('parameters', currentParam)

  axios.post(`${VPS_API_BASE}/sessionState.php`, formData, { timeout: 5000 })
    .then(() => {
      lastSavedParam = currentParam
      // URLバーを ?ss=1 に更新（session_stateからの復元マーカー）
      window.history.replaceState(null, 'map', window.location.pathname + '?ss=1')
    })
    .catch((err: unknown) => {
      console.warn('[sessionState] save failed:', err)
    })
}

/**
 * デバウンス付きでセッション保存をスケジュール
 * updatePermalink()から呼ばれる
 */
export function scheduleSessionSave(vm: VM): void {
  // @ts-ignore
  if (vm.$store.state.isOffline || vm.$store.state.isNetworkOffline) {
    cancelSessionSaveTimer()
    return
  }

  if (sessionSaveTimerId !== null) {
    clearTimeout(sessionSaveTimerId)
  }

  sessionSaveTimerId = window.setTimeout(() => {
    sessionSaveTimerId = null
    saveSessionState(vm)
  }, SESSION_SAVE_DELAY)
}

/**
 * セッション保存タイマーをキャンセル
 */
export function cancelSessionSaveTimer(): void {
  if (sessionSaveTimerId !== null) {
    clearTimeout(sessionSaveTimerId)
    sessionSaveTimerId = null
  }
}

/**
 * session_stateからuid+deviceで状態を読み込み
 */
export async function loadSessionState(uid: string): Promise<string | null> {
  try {
    const response = await axios.get(`${VPS_API_BASE}/sessionState.php`, {
      params: { uid, device: getDeviceId() },
      timeout: 5000,
    })
    if (response.data && typeof response.data === 'string') {
      return response.data
    }
    if (response.data?.error) {
      return null
    }
    return null
  } catch {
    console.warn('[sessionState] load failed')
    return null
  }
}
